// frontend/src/pages/StartAudit.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import { US_STATES as US_STATES_RAW } from "../components/universal-audit/constants";
import useEntitlement from "../hooks/useEntitlement";

const STORAGE_KEYS = {
  // Tier object (MVP stub)
  USER: "aifine_user",

  // Active business pointer
  ACTIVE_BUSINESS_ID: "aifine_active_business_id_v1",

  // Business registry
  BUSINESS_INDEX: "aifine_business_index_v1",
  BUSINESS_PAYLOAD_PREFIX: "aifine_business_payload_",

  // Compatibility shim (other pages depend on it)
  CONFIG: "aifine_config",

  // Per-business run state
  ANSWERS_PREFIX: "aifine_answers_business_",
  PROGRESS_PREFIX: "aifine_assessment_progress_business_",
};

const STANDARD_SINGLE_ID = "single_default";

function safeParse(raw, fallback = null) {
  try {
    if (!raw) return fallback;
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function safeArray(x) {
  return Array.isArray(x) ? x : [];
}

function cn(...xs) {
  return xs.filter(Boolean).join(" ");
}

function clamp(n, min, max) {
  const v = Number(n);
  if (!Number.isFinite(v)) return min;
  return Math.max(min, Math.min(max, v));
}

function safeGetItem(key) {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSetItem(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch {
    // ignore
  }
}

function safeRemoveItem(key) {
  try {
    localStorage.removeItem(key);
  } catch {
    // ignore
  }
}

function readSubscriptionTierFromLocal() {
  const obj = safeParse(safeGetItem(STORAGE_KEYS.USER), null);
  const tier = obj?.subscription_tier ?? obj?.subscriptionTier ?? obj?.tier ?? null;
  return tier ? String(tier) : "single";
}

function isTopTierLocal(tier) {
  return String(tier || "").toLowerCase() === "top";
}

function deriveCurrencyFromJurisdictions(selected) {
  const set = new Set(Array.isArray(selected) ? selected : []);
  if (set.has("EU")) return { code: "EUR", symbol: "€" };
  if (set.has("UK")) return { code: "GBP", symbol: "£" };
  return { code: "USD", symbol: "$" };
}

function formatMoney(n, symbol) {
  const num = Number(n);
  const safe = Number.isFinite(num) ? Math.round(num) : 0;
  return `${symbol}${safe.toLocaleString("en-US")}`;
}

function makeBusinessId() {
  // Stable enough for MVP: timestamp + random
  return `biz_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

function loadBusinessIndex() {
  const idx = safeParse(safeGetItem(STORAGE_KEYS.BUSINESS_INDEX), []);
  return Array.isArray(idx) ? idx : [];
}

function saveBusinessIndex(idx) {
  safeSetItem(STORAGE_KEYS.BUSINESS_INDEX, JSON.stringify(idx));
}

function saveBusinessPayload(businessId, payload) {
  safeSetItem(`${STORAGE_KEYS.BUSINESS_PAYLOAD_PREFIX}${businessId}`, JSON.stringify(payload));
}

function loadBusinessPayload(businessId) {
  return safeParse(safeGetItem(`${STORAGE_KEYS.BUSINESS_PAYLOAD_PREFIX}${businessId}`), null);
}

function setActiveBusinessIdStorage(businessId) {
  safeSetItem(STORAGE_KEYS.ACTIVE_BUSINESS_ID, String(businessId));
}

function getActiveBusinessIdStorage() {
  return safeGetItem(STORAGE_KEYS.ACTIVE_BUSINESS_ID) || "";
}

function clearBusinessRunState(businessId) {
  safeRemoveItem(`${STORAGE_KEYS.ANSWERS_PREFIX}${businessId}`);
  safeRemoveItem(`${STORAGE_KEYS.PROGRESS_PREFIX}${businessId}`);
}

// Piecewise slider curve for revenue: very granular at low end.
function revFromSlider(s, max) {
  const t = clamp(Number(s) || 0, 0, 1000) / 1000;
  // zones: 0–0.55 => 0–10M, 0.55–0.82 => 10M–200M, 0.82–1 => 200M–max
  if (t <= 0.55) {
    const u = t / 0.55;
    return Math.round(u * 10_000_000);
  }
  if (t <= 0.82) {
    const u = (t - 0.55) / (0.82 - 0.55);
    return Math.round(10_000_000 + u * (200_000_000 - 10_000_000));
  }
  const u = (t - 0.82) / (1 - 0.82);
  return Math.round(200_000_000 + Math.pow(u, 1.35) * (max - 200_000_000));
}

function sliderFromRev(v, max) {
  const val = clamp(Number(v) || 0, 0, max);
  if (val <= 10_000_000) {
    const u = 10_000_000 ? val / 10_000_000 : 0;
    return Math.round(u * 0.55 * 1000);
  }
  if (val <= 200_000_000) {
    const u = (val - 10_000_000) / (200_000_000 - 10_000_000);
    return Math.round((0.55 + u * (0.82 - 0.55)) * 1000);
  }
  const u = (val - 200_000_000) / (max - 200_000_000);
  const inv = Math.pow(clamp(u, 0, 1), 1 / 1.35);
  return Math.round((0.82 + inv * (1 - 0.82)) * 1000);
}

// People curve (gentle)
function peopleFromSlider(s, max) {
  const t = clamp(Number(s) || 0, 0, 1000) / 1000;
  const kneeT = 0.3;
  const kneeV = 0.05;
  if (t <= kneeT) {
    const u = t / kneeT;
    return Math.round(u * (max * kneeV));
  }
  const u = (t - kneeT) / (1 - kneeT);
  const gamma = 1.6;
  return Math.round(max * (kneeV + (1 - kneeV) * Math.pow(u, gamma)));
}

function sliderFromPeople(v, max) {
  const val = clamp(Number(v) || 0, 0, max);
  const kneeT = 0.3;
  const kneeV = 0.05;
  const kneeVal = max * kneeV;

  if (val <= kneeVal) {
    const u = kneeVal ? val / kneeVal : 0;
    return Math.round(u * kneeT * 1000);
  }
  const u = (val - kneeVal) / (max - kneeVal);
  const gamma = 1.6;
  const inv = Math.pow(clamp(u, 0, 1), 1 / gamma);
  return Math.round((kneeT + (1 - kneeT) * inv) * 1000);
}

export default function StartAudit() {
  const navigate = useNavigate();
  const location = useLocation();

  /**
   * ✅ Consolidated paywall gating:
   * Auth + paid checks are enforced globally by PaywallGuard (App.jsx).
   * This page should not redirect to /auth or /pricing.
   * We still use entitlement ONLY for capability tier (top vs standard).
   */

  // Fast local fallback (for responsiveness), then entitlement hook becomes authoritative.
  const localTier = useMemo(() => {
    try {
      return readSubscriptionTierFromLocal();
    } catch {
      return "single";
    }
  }, []);

  const ent = useEntitlement();
  const entitlementTier = ent?.tier ?? null;
  const entitlementIsTop = typeof ent?.isTopTier === "boolean" ? ent.isTopTier : null;

  const effectiveTier = useMemo(() => {
    return entitlementTier ? String(entitlementTier) : String(localTier || "single");
  }, [entitlementTier, localTier]);

  const top = useMemo(() => {
    if (typeof entitlementIsTop === "boolean") return entitlementIsTop;
    return isTopTierLocal(effectiveTier);
  }, [entitlementIsTop, effectiveTier]);

  const mode = useMemo(() => {
    const params = new URLSearchParams(location.search || "");
    const m = String(params.get("mode") || "").toLowerCase();
    const requested = m === "new" ? "new" : "rerun";
    if (!top && requested === "new") return "rerun";
    return requested;
  }, [location.search, top]);

  // Data
  const [businessIndex, setBusinessIndex] = useState([]);
  const [activeBusinessId, setActiveBusinessIdState] = useState("");

  // Form state
  const [editingBusinessId, setEditingBusinessId] = useState("");
  const [clientName, setClientName] = useState("");
  const [industry, setIndustry] = useState("");
  const [niche, setNiche] = useState("");
  const [annualTurnover, setAnnualTurnover] = useState(0);
  const [userImpact, setUserImpact] = useState(0);
  const [jurisdictions, setJurisdictions] = useState([]);
  const [selectedStates, setSelectedStates] = useState(new Set());

  const US_STATES = safeArray(US_STATES_RAW);
  const REV_MAX = 5_000_000_000;
  const PEOPLE_MAX = 500_000;

  const currency = useMemo(() => deriveCurrencyFromJurisdictions(jurisdictions), [jurisdictions]);
  const shouldUseStates = useMemo(
    () => Array.isArray(jurisdictions) && jurisdictions.includes("US_STATE"),
    [jurisdictions]
  );

  const [revSlider, setRevSlider] = useState(0);
  const [peopleSlider, setPeopleSlider] = useState(0);

  // ✅ Standard-tier hardening (logic-only):
  // Ensure Standard tier always uses a single stable business id and keeps CONFIG aligned.
  useEffect(() => {
    if (top) return;

    try {
      setActiveBusinessIdStorage(STANDARD_SINGLE_ID);
      setActiveBusinessIdState(STANDARD_SINGLE_ID);
      setEditingBusinessId(STANDARD_SINGLE_ID);

      const payload = loadBusinessPayload(STANDARD_SINGLE_ID);
      if (payload) {
        // Keep CONFIG shim consistent
        safeSetItem(STORAGE_KEYS.CONFIG, JSON.stringify(payload));
      } else {
        // Ensure CONFIG exists even before first save
        const cfg = safeParse(safeGetItem(STORAGE_KEYS.CONFIG), null);
        if (cfg && typeof cfg === "object") {
          saveBusinessPayload(STANDARD_SINGLE_ID, cfg);
          safeSetItem(STORAGE_KEYS.CONFIG, JSON.stringify(cfg));
        }
      }
    } catch {
      // ignore
    }
  }, [top]);

  // Hydrate index + active
  useEffect(() => {
    const idx = loadBusinessIndex();
    setBusinessIndex(idx);

    const active = getActiveBusinessIdStorage();
    setActiveBusinessIdState(active);

    // If no active but index exists, pick first as active
    if (!active && idx.length) {
      const first = idx[0]?.business_id;
      if (first) {
        setActiveBusinessIdStorage(first);
        setActiveBusinessIdState(first);
      }
    }
  }, []);

  // Load form based on mode/top tier
  useEffect(() => {
    if (!top) {
      // Standard: single business only
      const active = STANDARD_SINGLE_ID;
      setActiveBusinessIdStorage(active);
      setActiveBusinessIdState(active);

      const payload = loadBusinessPayload(active);
      if (payload) {
        setEditingBusinessId(active);
        setClientName(payload.clientName || "");
        setIndustry(payload.industry || "");
        setNiche(payload.niche || "");
        setAnnualTurnover(Number(payload.annualTurnover) || 0);
        setUserImpact(Number(payload.userImpact) || 0);
        setJurisdictions(Array.isArray(payload.jurisdictions) ? payload.jurisdictions : []);
        const st = Array.isArray(payload.selectedStates) ? payload.selectedStates : [];
        setSelectedStates(new Set(st.map((x) => String(x).toUpperCase())));
      } else {
        setEditingBusinessId(active);
        setClientName("");
        setIndustry("");
        setNiche("");
        setAnnualTurnover(0);
        setUserImpact(0);
        setJurisdictions([]);
        setSelectedStates(new Set());
      }
      return;
    }

    // Top tier
    if (mode === "new") {
      setEditingBusinessId("");
      setClientName("");
      setIndustry("");
      setNiche("");
      setAnnualTurnover(0);
      setUserImpact(0);
      setJurisdictions([]);
      setSelectedStates(new Set());
      return;
    }

    // rerun: load active business into form
    const active = getActiveBusinessIdStorage();
    if (!active) return;
    const payload = loadBusinessPayload(active);
    if (!payload) return;

    setEditingBusinessId(active);
    setClientName(payload.clientName || "");
    setIndustry(payload.industry || "");
    setNiche(payload.niche || "");
    setAnnualTurnover(Number(payload.annualTurnover) || 0);
    setUserImpact(Number(payload.userImpact) || 0);
    setJurisdictions(Array.isArray(payload.jurisdictions) ? payload.jurisdictions : []);
    const st = Array.isArray(payload.selectedStates) ? payload.selectedStates : [];
    setSelectedStates(new Set(st.map((x) => String(x).toUpperCase())));
  }, [top, mode]);

  // Slider sync
  useEffect(() => {
    setRevSlider(sliderFromRev(annualTurnover, REV_MAX));
  }, [annualTurnover]);

  useEffect(() => {
    setPeopleSlider(sliderFromPeople(userImpact, PEOPLE_MAX));
  }, [userImpact]);

  function toggleJur(code) {
    setJurisdictions((prev) => {
      const next = new Set(Array.isArray(prev) ? prev : []);
      next.has(code) ? next.delete(code) : next.add(code);
      if (!next.has("US_STATE")) setSelectedStates(new Set());
      return Array.from(next);
    });
  }

  function toggleState(code) {
    const c = String(code || "").toUpperCase();
    if (!c) return;
    setSelectedStates((prev) => {
      const next = new Set(prev);
      next.has(c) ? next.delete(c) : next.add(c);
      return next;
    });
  }

  function clearStates() {
    setSelectedStates(new Set());
  }

  function selectAllStates() {
    setSelectedStates(new Set(US_STATES.map(([c]) => String(c).toUpperCase())));
  }

  const isValid = useMemo(() => {
    const baseOk =
      String(clientName || "").trim().length > 0 &&
      String(industry || "").trim().length > 0 &&
      String(niche || "").trim().length > 0 &&
      Array.isArray(jurisdictions) &&
      jurisdictions.length > 0;

    if (!baseOk) return false;
    if (shouldUseStates) return (selectedStates?.size || 0) > 0;
    return true;
  }, [clientName, industry, niche, jurisdictions, shouldUseStates, selectedStates]);

  function persistBusiness(businessId) {
    const payload = {
      business_id: businessId,
      clientName: String(clientName || "").trim(),
      industry: String(industry || "").trim(),
      niche: String(niche || "").trim(),
      annualTurnover: Number(annualTurnover) || 0,
      userImpact: Number(userImpact) || 0,
      jurisdictions: Array.isArray(jurisdictions) ? jurisdictions : [],
      selectedStates:
        Array.isArray(jurisdictions) && jurisdictions.includes("US_STATE") ? Array.from(selectedStates || []) : [],
      currency_code: currency.code,
      currency_symbol: currency.symbol,
      updated_at_iso: new Date().toISOString(),
    };

    saveBusinessPayload(businessId, payload);

    // Compatibility shim points to ACTIVE business
    safeSetItem(STORAGE_KEYS.CONFIG, JSON.stringify(payload));

    return payload;
  }

  function upsertIndexEntry(businessId, payload) {
    const idx = loadBusinessIndex();
    const next = Array.isArray(idx) ? [...idx] : [];
    const i = next.findIndex((x) => x?.business_id === businessId);
    const entry = {
      business_id: businessId,
      entity_name: payload?.clientName || "Unnamed",
      jurisdictions: Array.isArray(payload?.jurisdictions) ? payload.jurisdictions : [],
      updated_at_iso: payload?.updated_at_iso || new Date().toISOString(),
    };
    if (i >= 0) next[i] = entry;
    else next.unshift(entry);

    saveBusinessIndex(next);
    setBusinessIndex(next);
  }

  function selectBusiness(businessId) {
    const payload = loadBusinessPayload(businessId);
    if (!payload) return;

    setActiveBusinessIdStorage(businessId);
    setActiveBusinessIdState(businessId);

    // Update shim
    safeSetItem(STORAGE_KEYS.CONFIG, JSON.stringify(payload));

    setEditingBusinessId(businessId);
    setClientName(payload.clientName || "");
    setIndustry(payload.industry || "");
    setNiche(payload.niche || "");
    setAnnualTurnover(Number(payload.annualTurnover) || 0);
    setUserImpact(Number(payload.userImpact) || 0);
    setJurisdictions(Array.isArray(payload.jurisdictions) ? payload.jurisdictions : []);
    const st = Array.isArray(payload.selectedStates) ? payload.selectedStates : [];
    setSelectedStates(new Set(st.map((x) => String(x).toUpperCase())));

    navigate("/start-audit?mode=rerun");
  }

  function startAssessment() {
    if (!isValid) return;

    let businessId = editingBusinessId;

    // Standard tier is always single_default (hard guard)
    if (!top) {
      businessId = STANDARD_SINGLE_ID;
    } else if (top && mode === "new") {
      businessId = makeBusinessId();
    }

    if (!businessId) {
      // fallback safety (top tier)
      businessId = getActiveBusinessIdStorage() || makeBusinessId();
    }

    // Persist + set active
    const payload = persistBusiness(businessId);
    setActiveBusinessIdStorage(businessId);
    setActiveBusinessIdState(businessId);

    // Only top tier maintains index
    if (top) upsertIndexEntry(businessId, payload);

    // IMPORTANT: do NOT wipe other businesses’ progress.
    // We start a NEW run for THIS business only:
    clearBusinessRunState(businessId);

    navigate("/assessment");
  }

  const chipBase =
    "inline-flex items-center justify-center rounded-xl border px-4 py-2 text-sm font-semibold transition-colors";
  const selectedChip = "bg-emerald-50 border-emerald-200 text-emerald-900";
  const unselectedChip = "bg-white border-slate-200 text-slate-700 hover:bg-slate-50";

  // ✅ Don’t depend on businessIndex.length for something sourced from localStorage.
  // This avoids stale labels and avoids lint-y dependency tricks.
  const activePayload = useMemo(() => {
    if (!activeBusinessId) return null;
    return loadBusinessPayload(activeBusinessId);
  }, [activeBusinessId]);

  const activeLabel = useMemo(() => {
    return activePayload?.clientName ? String(activePayload.clientName) : "";
  }, [activePayload]);

  function jurDisplay(jArr) {
    return Array.isArray(jArr) && jArr.length ? jArr.join(", ") : "—";
  }

  return (
    <div className="flex h-screen bg-slate-50 text-slate-900 overflow-hidden">
      <Sidebar />

      <main className="flex-1 overflow-auto">
        <div className="mx-auto max-w-6xl px-8 py-10">
          {/* Header */}
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold tracking-tight">Start Audit</h1>
              <p className="mt-1 text-sm text-slate-600">
                {top
                  ? "Top tier: manage multiple business profiles and run audits per entity."
                  : "Standard tier: single business profile (one entity)."}
              </p>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => navigate("/dashboard")}
                className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold hover:bg-slate-50"
              >
                Back to Dashboard
              </button>
            </div>
          </div>

          {/* Top-tier business selector */}
          {top ? (
            <div className="mt-6 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-sm font-semibold text-slate-900">Active business</div>
                  <div className="mt-1 text-xs text-slate-500">
                    Businesses appear here after you click{" "}
                    <span className="font-semibold">Save Business & Start Assessment</span>.
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => navigate("/start-audit?mode=new")}
                    className={cn(
                      "rounded-xl border px-4 py-2 text-sm font-semibold",
                      mode === "new" ? "border-slate-900 bg-slate-50" : "border-slate-200 bg-white hover:bg-slate-50"
                    )}
                  >
                    New Business
                  </button>

                  <button
                    type="button"
                    onClick={() => navigate("/start-audit?mode=rerun")}
                    className={cn(
                      "rounded-xl border px-4 py-2 text-sm font-semibold",
                      mode === "rerun"
                        ? "border-slate-900 bg-slate-50"
                        : "border-slate-200 bg-white hover:bg-slate-50"
                    )}
                    disabled={!businessIndex.length}
                    title={!businessIndex.length ? "Create at least one business first." : "Edit active business profile"}
                  >
                    Edit Active
                  </button>
                </div>
              </div>

              <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
                {activeLabel ? (
                  <>
                    Active: <span className="font-black text-slate-900">{activeLabel}</span>
                  </>
                ) : (
                  <span>No active business yet.</span>
                )}
              </div>

              {businessIndex.length ? (
                <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200 bg-white">
                  <div className="grid grid-cols-12 bg-slate-50 px-4 py-3 text-[10px] font-black uppercase tracking-[0.25em] text-slate-500">
                    <div className="col-span-4">Entity</div>
                    <div className="col-span-5">Jurisdictions</div>
                    <div className="col-span-2">Updated</div>
                    <div className="col-span-1 text-right">Action</div>
                  </div>

                  {businessIndex.map((r) => {
                    const isActive = r.business_id === activeBusinessId;
                    return (
                      <div
                        key={r.business_id}
                        className="grid grid-cols-12 items-center px-4 py-3 border-t border-slate-100 text-sm"
                      >
                        <div className="col-span-4 flex items-center gap-2">
                          <span className="font-semibold text-slate-900">{r.entity_name || "Unnamed"}</span>
                          {isActive ? (
                            <span className="rounded-full bg-emerald-50 border border-emerald-200 px-2 py-0.5 text-[10px] font-black text-emerald-800">
                              ACTIVE
                            </span>
                          ) : null}
                        </div>
                        <div className="col-span-5 text-slate-600">{jurDisplay(r.jurisdictions)}</div>
                        <div className="col-span-2 text-slate-500 text-xs">
                          {r.updated_at_iso ? new Date(r.updated_at_iso).toLocaleString() : "—"}
                        </div>
                        <div className="col-span-1 text-right">
                          <button
                            type="button"
                            onClick={() => selectBusiness(r.business_id)}
                            className="rounded-xl border border-slate-200 bg-white px-3 py-1 text-xs font-black text-slate-700 hover:bg-slate-50"
                          >
                            Select
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-600">
                  No businesses stored yet. Click <span className="font-semibold">New Business</span> to create your first
                  entity.
                </div>
              )}
            </div>
          ) : null}

          {/* Inputs card */}
          <div className="mt-6 rounded-3xl border border-slate-200 bg-white shadow-sm">
            <div className="px-8 py-6 border-b border-slate-100">
              <div className="text-sm font-semibold text-slate-900">
                {top && mode === "new" ? "New business profile" : "Business profile"}
              </div>
              <div className="mt-1 text-xs text-slate-500">
                Fields marked <span className="text-red-500 font-bold">*</span> must be completed before you can continue.
              </div>
            </div>

            <div className="px-8 py-8 space-y-8">
              {/* Entity Name */}
              <div>
                <div className="text-sm font-semibold text-slate-900">
                  Entity Name <span className="text-red-500">*</span>
                </div>
                <input
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  placeholder="Legal entity name"
                  className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none focus:border-slate-300"
                />
              </div>

              {/* Jurisdictions */}
              <div>
                <div className="text-sm font-semibold text-slate-900">
                  Jurisdictions <span className="text-red-500">*</span>
                </div>
                <div className="mt-2 text-xs text-slate-500">Select all regulatory regimes that may apply.</div>

                <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { code: "US_FED", label: "US (FED)" },
                    { code: "US_STATE", label: "US (STATE)" },
                    { code: "UK", label: "UK" },
                    { code: "EU", label: "EU" },
                  ].map((j) => {
                    const on = jurisdictions.includes(j.code);
                    return (
                      <button
                        key={j.code}
                        type="button"
                        onClick={() => toggleJur(j.code)}
                        className={cn(chipBase, on ? selectedChip : unselectedChip)}
                      >
                        {j.label}
                      </button>
                    );
                  })}
                </div>

                <div className="mt-3 text-xs text-slate-500">
                  Selected:{" "}
                  <span className="font-semibold text-slate-700">
                    {jurisdictions.length ? jurisdictions.join(", ") : "—"}
                  </span>
                </div>
              </div>

              {/* US State Selector */}
              {shouldUseStates ? (
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6">
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <div className="text-sm font-semibold text-slate-900">
                        U.S. States <span className="text-red-500">*</span>
                      </div>
                      <div className="mt-1 text-xs text-slate-500">Select the states where your AI impacts people.</div>
                      {(selectedStates?.size || 0) === 0 ? (
                        <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-900">
                          Select at least one state to continue.
                        </div>
                      ) : null}
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={selectAllStates}
                        className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:border-slate-300"
                      >
                        Select All
                      </button>
                      <button
                        type="button"
                        onClick={clearStates}
                        className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:border-slate-300"
                      >
                        Clear
                      </button>
                    </div>
                  </div>

                  <div className="mt-4 max-h-72 overflow-auto rounded-2xl border border-slate-200 bg-white p-2">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      {US_STATES.map(([code, name]) => {
                        const c = String(code || "").toUpperCase();
                        const selected = selectedStates?.has?.(c);
                        return (
                          <button
                            key={c}
                            type="button"
                            onClick={() => toggleState(c)}
                            className={cn(
                              "rounded-2xl border p-3 text-left transition-colors",
                              selected
                                ? "border-slate-900 bg-slate-50"
                                : "border-slate-200 bg-white hover:border-slate-300"
                            )}
                          >
                            <div className="text-xs font-semibold text-slate-900">{c}</div>
                            <div className="text-[11px] text-slate-600">{name}</div>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="mt-3 text-xs text-slate-500">
                    Selected: <span className="font-semibold text-slate-700">{selectedStates?.size || 0}</span>
                  </div>
                </div>
              ) : null}

              {/* Industry + Niche */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <div className="text-sm font-semibold text-slate-900">
                    Industry <span className="text-red-500">*</span>
                  </div>
                  <input
                    value={industry}
                    onChange={(e) => setIndustry(e.target.value)}
                    placeholder="e.g., Healthcare, Financial Services"
                    className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none focus:border-slate-300"
                  />
                </div>

                <div>
                  <div className="text-sm font-semibold text-slate-900">
                    Niche <span className="text-red-500">*</span>
                  </div>
                  <input
                    value={niche}
                    onChange={(e) => setNiche(e.target.value)}
                    placeholder="e.g., Medical Billing, Lending, HR Screening"
                    className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none focus:border-slate-300"
                  />
                </div>
              </div>

              {/* Annual Revenue slider */}
              <div className="rounded-2xl border border-slate-200 bg-white p-6">
                <div className="flex items-center justify-between gap-4">
                  <div className="text-sm font-semibold text-slate-900">
                    Annual Revenue Estimate ({currency.code}) <span className="text-red-500">*</span>
                  </div>
                  <div className="text-lg font-extrabold text-blue-700">{formatMoney(annualTurnover, currency.symbol)}</div>
                </div>

                <input
                  type="range"
                  min={0}
                  max={1000}
                  value={revSlider}
                  onChange={(e) => {
                    const s = clamp(e.target.value, 0, 1000);
                    setRevSlider(s);
                    setAnnualTurnover(revFromSlider(s, REV_MAX));
                  }}
                  className="mt-4 w-full"
                />

                <div className="mt-2 flex items-center justify-between text-xs text-slate-500">
                  <div>{currency.symbol}0</div>
                  <div>
                    Max: <span className="font-semibold text-slate-700">{formatMoney(REV_MAX, currency.symbol)}</span>
                  </div>
                </div>

                <div className="mt-2 text-[11px] text-slate-500">
                  Granularity zones: 0–10M (fine), 10M–200M (mid), 200M–5B (enterprise).
                </div>
              </div>

              {/* People impacted slider */}
              <div className="rounded-2xl border border-slate-200 bg-white p-6">
                <div className="flex items-center justify-between gap-4">
                  <div className="text-sm font-semibold text-slate-900">
                    People Impacted Per Month <span className="text-red-500">*</span>
                  </div>
                  <div className="text-lg font-extrabold text-blue-700">{Number(userImpact).toLocaleString("en-US")}</div>
                </div>

                <input
                  type="range"
                  min={0}
                  max={1000}
                  value={peopleSlider}
                  onChange={(e) => {
                    const s = clamp(e.target.value, 0, 1000);
                    setPeopleSlider(s);
                    setUserImpact(peopleFromSlider(s, PEOPLE_MAX));
                  }}
                  className="mt-4 w-full"
                />

                <div className="mt-2 flex items-center justify-between text-xs text-slate-500">
                  <div>0</div>
                  <div>
                    Max: <span className="font-semibold text-slate-700">{PEOPLE_MAX.toLocaleString("en-US")}</span>
                  </div>
                </div>
              </div>

              {/* CTA */}
              <div className="pt-6 border-t border-slate-100 flex items-center justify-between gap-4">
                <div className="text-xs text-slate-500">
                  {top && mode === "new"
                    ? "This will create a new business and begin a new assessment run."
                    : "This will begin a new run for the selected business."}
                </div>

                <button
                  type="button"
                  disabled={!isValid}
                  onClick={startAssessment}
                  className={cn(
                    "rounded-xl px-5 py-2 text-sm font-semibold",
                    isValid ? "bg-slate-900 text-white hover:bg-slate-800" : "bg-slate-200 text-slate-500 cursor-not-allowed"
                  )}
                >
                  {top && mode === "new" ? "Save Business & Start Assessment" : "Start Assessment Run"}
                </button>
              </div>

              {!isValid ? (
                <div className="text-xs text-slate-500">
                  {!jurisdictions?.length
                    ? "Select at least one jurisdiction to continue."
                    : shouldUseStates && (selectedStates?.size || 0) === 0
                    ? "US (STATE) selected — please choose at least one state."
                    : "Complete required fields to continue."}
                </div>
              ) : null}
            </div>
          </div>

          <div className="mt-10 pb-8 text-xs text-slate-400">© 2026 Advanced Advisory Services, LLC. All Rights Reserved.</div>
        </div>
      </main>
    </div>
  );
}